import React, { useState, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Upload, X, Image as ImageIcon, Plus, Check } from 'lucide-react';
import { toast } from 'sonner';

import { BACKEND_URL } from '../config/api';
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('lms_token')}`
});

const SUBJECTS = ['English', 'Maths', 'Science', 'SST', 'Hindi', 'Computer Science', 'Robotics', 'Coding', 'Arts', 'Music'];
const GRADES = ['1st', '2nd', '3rd', '4th', '5th', '6th', '7th', '8th', '9th', '10th', '11th', '12th'];
const BOARDS = ['CBSE', 'ICSE', 'SSC', 'State Board', 'IB'];
const DURATION_UNITS = ['Hours', 'Days', 'Weeks', 'Months'];

export default function CourseMetadataForm({ courseData, onSave }) {
  const [formData, setFormData] = useState({
    title: courseData?.title || '',
    description: courseData?.description || '',
    thumbnail_url: courseData?.thumbnail_url || '',
    skills_learned: courseData?.skills_learned || '',
    course_includes: courseData?.course_includes || [],
    subjects_covered: courseData?.subjects_covered || [],
    grade_levels: courseData?.grade_levels || [],
    duration_value: courseData?.duration_value || '',
    duration_unit: courseData?.duration_unit || 'Weeks',
    board_alignment: courseData?.board_alignment || ''
  });
  
  const [uploading, setUploading] = useState(false);
  const [newIncludeItem, setNewIncludeItem] = useState('');
  const fileInputRef = useRef(null);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleThumbnailUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please upload a .jpg or .png file');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large — max 10 MB');
      return;
    }

    setUploading(true);
    try {
      const formDataFile = new FormData();
      formDataFile.append('file', file);

      const res = await axios.post(`${API}/courses/upload-thumbnail`, formDataFile, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        }
      });

      handleChange('thumbnail_url', res.data.thumbnail_url);
      toast.success('Thumbnail uploaded successfully!');
    } catch (error) {
      toast.error('Failed to upload thumbnail');
    } finally {
      setUploading(false);
    }
  };

  const addIncludeItem = () => {
    if (newIncludeItem.trim()) {
      handleChange('course_includes', [...formData.course_includes, newIncludeItem.trim()]);
      setNewIncludeItem('');
    }
  };

  const removeIncludeItem = (index) => {
    handleChange('course_includes', formData.course_includes.filter((_, i) => i !== index));
  };

  const toggleSubject = (subject) => {
    const current = formData.subjects_covered;
    if (current.includes(subject)) {
      handleChange('subjects_covered', current.filter(s => s !== subject));
    } else {
      handleChange('subjects_covered', [...current, subject]);
    }
  };

  const toggleGrade = (grade) => {
    const current = formData.grade_levels;
    if (current.includes(grade)) {
      handleChange('grade_levels', current.filter(g => g !== grade));
    } else {
      handleChange('grade_levels', [...current, grade]);
    }
  };

  const getThumbnailUrl = () => {
    if (!formData.thumbnail_url) return null;
    if (formData.thumbnail_url.startsWith('http')) return formData.thumbnail_url;
    return `${BACKEND_URL}${formData.thumbnail_url}`;
  };

  const handleSave = () => {
    if (!formData.title || !formData.description) {
      toast.error('Title and description are required');
      return;
    }
    onSave(formData);
  };

  return (
    <div className="space-y-6">
      {/* Basic Info */}
      <Card className="border-0 card-shadow bg-white rounded-2xl">
        <CardHeader>
          <CardTitle className="text-xl font-bold text-navy">Basic Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label className="text-[15px] font-semibold text-navy">Course Title <span className="text-red-500">*</span></Label>
            <Input
              value={formData.title}
              onChange={(e) => handleChange('title', e.target.value)}
              placeholder="e.g., Drone Programming Masterclass"
              className="h-12 border-2 border-[#E7E7E7] rounded-xl focus:border-primary-blue transition-smooth"
            />
          </div>
          
          <div className="space-y-2">
            <Label className="text-[15px] font-semibold text-navy">Description <span className="text-red-500">*</span></Label>
            <Textarea
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Describe what students will learn in this course..."
              rows={4}
              className="border-2 border-[#E7E7E7] rounded-xl focus:border-primary-blue transition-smooth"
            />
          </div>
        </CardContent>
      </Card>

      {/* Thumbnail Upload */}
      <Card className="border-0 card-shadow bg-white rounded-2xl">
        <CardHeader>
          <CardTitle className="text-xl font-bold text-navy">Course Thumbnail</CardTitle>
          <p className="text-sm text-[#4D4D4D] font-light mt-1">Recommended size: 1200×800 px (JPG, PNG)</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <input
            ref={fileInputRef}
            type="file"
            accept=".jpg,.jpeg,.png"
            onChange={handleThumbnailUpload}
            className="hidden"
          />
          
          {formData.thumbnail_url ? (
            <div className="space-y-3">
              <div className="relative rounded-2xl overflow-hidden border-2 border-[#E7E7E7]">
                <img src={getThumbnailUrl()} alt="Course thumbnail" className="w-full h-64 object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
              </div>
              <div className="flex space-x-2">
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  variant="outline"
                  className="flex-1 border-2 border-[#E7E7E7] rounded-xl hover:border-primary-blue transition-smooth"
                >
                  <Upload className="w-4 h-4 mr-2" strokeWidth={2} />
                  Replace
                </Button>
                <Button
                  onClick={() => handleChange('thumbnail_url', '')}
                  disabled={uploading}
                  variant="outline"
                  className="flex-1 border-2 border-[#E7E7E7] text-red-600 rounded-xl hover:border-red-600 transition-smooth"
                >
                  <X className="w-4 h-4 mr-2" strokeWidth={2} />
                  Remove
                </Button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="w-full h-64 border-2 border-dashed border-[#E7E7E7] rounded-2xl flex flex-col items-center justify-center hover:border-primary-blue hover:bg-[#F7F7F7] transition-smooth"
            >
              <ImageIcon className="w-12 h-12 text-[#A5A5A5] mb-3" strokeWidth={1.5} />
              <p className="text-[15px] font-semibold text-navy mb-1">
                {uploading ? 'Uploading...' : 'Click to upload thumbnail'}
              </p>
              <p className="text-sm text-[#A5A5A5]">JPG, PNG up to 10 MB</p>
            </button>
          )}
        </CardContent>
      </Card>

      {/* Skills Learned */}
      <Card className="border-0 card-shadow bg-white rounded-2xl">
        <CardHeader>
          <CardTitle className="text-xl font-bold text-navy">Skills Learned</CardTitle>
        </CardHeader>
        <CardContent>
          <Textarea
            value={formData.skills_learned}
            onChange={(e) => handleChange('skills_learned', e.target.value)}
            placeholder="• Problem Solving
• Logical Thinking
• Drone Mechanics
• Programming Basics"
            rows={5}
            className="border-2 border-[#E7E7E7] rounded-xl focus:border-primary-blue transition-smooth"
          />
        </CardContent>
      </Card>

      {/* Course Includes */}
      <Card className="border-0 card-shadow bg-white rounded-2xl">
        <CardHeader>
          <CardTitle className="text-xl font-bold text-navy">What's Included</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex space-x-2">
            <Input
              value={newIncludeItem}
              onChange={(e) => setNewIncludeItem(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && addIncludeItem()}
              placeholder="e.g., 10 Video Lessons"
              className="h-12 border-2 border-[#E7E7E7] rounded-xl focus:border-primary-blue transition-smooth"
            />
            <Button
              onClick={addIncludeItem}
              className="bg-primary-blue hover:bg-[#1874CD] rounded-xl btn-press"
            >
              <Plus className="w-5 h-5" strokeWidth={2} />
            </Button>
          </div>
          
          {formData.course_includes.length > 0 && (
            <div className="space-y-2">
              {formData.course_includes.map((item, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-[#F7F7F7] rounded-xl">
                  <div className="flex items-center space-x-2">
                    <Check className="w-4 h-4 text-primary-blue" strokeWidth={2} />
                    <span className="text-[15px] text-navy">{item}</span>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => removeIncludeItem(index)}
                    className="h-8 w-8 p-0 hover:bg-red-100"
                  >
                    <X className="w-4 h-4 text-red-600" strokeWidth={2} />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Subjects Covered */}
      <Card className="border-0 card-shadow bg-white rounded-2xl">
        <CardHeader>
          <CardTitle className="text-xl font-bold text-navy">Subjects Covered</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {SUBJECTS.map(subject => (
              <Badge
                key={subject}
                onClick={() => toggleSubject(subject)}
                className={`px-4 py-2 cursor-pointer transition-smooth ${
                  formData.subjects_covered.includes(subject)
                    ? 'bg-primary-blue text-white hover:bg-[#1874CD]'
                    : 'bg-[#F7F7F7] text-navy hover:bg-[#E7E7E7]'
                }`}
              >
                {subject}
              </Badge>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Grade Levels */}
      <Card className="border-0 card-shadow bg-white rounded-2xl">
        <CardHeader>
          <CardTitle className="text-xl font-bold text-navy">Grade Levels</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {GRADES.map(grade => (
              <Badge
                key={grade}
                onClick={() => toggleGrade(grade)}
                className={`px-4 py-2 cursor-pointer transition-smooth ${
                  formData.grade_levels.includes(grade)
                    ? 'bg-primary-blue text-white hover:bg-[#1874CD]'
                    : 'bg-[#F7F7F7] text-navy hover:bg-[#E7E7E7]'
                }`}
              >
                {grade}
              </Badge>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Duration & Board */}
      <Card className="border-0 card-shadow bg-white rounded-2xl">
        <CardHeader>
          <CardTitle className="text-xl font-bold text-navy">Duration & Board</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-[15px] font-semibold text-navy">Duration</Label>
              <Input
                type="number"
                value={formData.duration_value}
                onChange={(e) => handleChange('duration_value', e.target.value)}
                placeholder="e.g., 8"
                className="h-12 border-2 border-[#E7E7E7] rounded-xl focus:border-primary-blue transition-smooth"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[15px] font-semibold text-navy">Unit</Label>
              <Select value={formData.duration_unit} onValueChange={(value) => handleChange('duration_unit', value)}>
                <SelectTrigger className="h-12 border-2 border-[#E7E7E7] rounded-xl focus:border-primary-blue transition-smooth">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DURATION_UNITS.map(unit => (
                    <SelectItem key={unit} value={unit}>{unit}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-[15px] font-semibold text-navy">Board Alignment</Label>
            <Select value={formData.board_alignment} onValueChange={(value) => handleChange('board_alignment', value)}>
              <SelectTrigger className="h-12 border-2 border-[#E7E7E7] rounded-xl focus:border-primary-blue transition-smooth">
                <SelectValue placeholder="Select board" />
              </SelectTrigger>
              <SelectContent>
                {BOARDS.map(board => (
                  <SelectItem key={board} value={board}>{board}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end space-x-3">
        <Button
          onClick={handleSave}
          className="bg-primary-blue hover:bg-[#1874CD] text-white font-semibold px-8 py-3 rounded-xl transition-smooth btn-press"
        >
          Save Course Details
        </Button>
      </div>
    </div>
  );
}
